import React, { useState } from 'react';
import { useUa } from './UserAdminContext';
import * as api from './userAdminApi';
import Icon from './UaIcon';
import { Sheet } from './UaOverlay';
import { Avatar, Changes, DeptPicker, Field, PageSwitch, PersonStatus } from './UaBits';
import {
    LV, actorLabel, allPages, dayMs, deptList, fmtDate, fmtShort, fullName, labelOf, lvlOf, ms, nOverrides, openCount, personById, plural, rel, roleById, stKey,
} from './uaModel';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PROFILE = [['first', 'First name'], ['last', 'Last name'], ['email', 'Work email'], ['title', 'Job title'], ['dept', 'Department']];
const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
const clean = (key, value) => (key === 'email' ? String(value || '').trim().toLowerCase() : String(value || '').trim());

/** Edit one user: details, role and page access, account status and recent history. One Save for all of it. */
export default function PersonPanel({ id }) {
    const { catalog, data, meId, run, closePanel, openModal, actions, setActFilters, goTab } = useUa();
    const person = personById(data, id);
    const [draft, setDraft] = useState(() => (person ? {
        first: person.first, last: person.last, email: person.email, title: person.title || '', dept: person.dept, role: person.role, overrides: { ...person.overrides },
    } : null));
    const [showPages, setShowPages] = useState(() => !!person && nOverrides(person) > 0);
    const [errors, setErrors] = useState({});
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    if (!person || !draft) return null;

    const isMe = person.id === meId;
    const role = roleById(data, draft.role) || data.roles[0];
    const pages = allPages(catalog);
    const profileChanged = PROFILE.filter(([k]) => clean(k, draft[k]) !== (person[k] || ''));
    const roleChanged = draft.role !== person.role;
    const pageChanges = pages.filter((pg) => (roleChanged
        ? has(draft.overrides, pg.id)
        : lvlOf(data, person.role, person.overrides, pg.id) !== lvlOf(data, draft.role, draft.overrides, pg.id)));
    const nChanges = profileChanged.length + (roleChanged ? 1 : 0) + pageChanges.length;
    const custom = Object.keys(draft.overrides).length;
    const first = draft.first.trim() || person.first;

    const edit = (key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
        setErrors((old) => { const next = { ...old }; delete next[key]; return next; });
        setConfirm(false);
    };
    // A different role starts from that role's pages; going back to their role brings back their own changes.
    const setRole = (r) => {
        setDraft((d) => ({ ...d, role: r, overrides: r === person.role ? { ...person.overrides } : {} }));
        setErrors((old) => { const next = { ...old }; delete next.role; return next; });
        setConfirm(false);
    };
    const setPage = (pageId, level) => {
        setDraft((d) => {
            const overrides = { ...d.overrides };
            if (level === (roleById(data, d.role) || {}).levels[pageId]) delete overrides[pageId]; else overrides[pageId] = level;
            return { ...d, overrides };
        });
        setConfirm(false);
    };

    const requestClose = () => { if (nChanges && !confirm) setConfirm(true); else closePanel(); };

    const save = async () => {
        const err = {};
        if (!draft.first.trim()) err.first = 'Enter a first name.';
        if (!draft.last.trim()) err.last = 'Enter a last name.';
        const email = clean('email', draft.email);
        if (!EMAIL_RE.test(email)) err.email = 'Enter a valid email.';
        else if (data.people.some((p) => p.email === email && p.id !== person.id)) err.email = 'Someone else already uses this email.';
        if (!deptList(draft.dept).length) err.dept = 'Pick at least one department.';
        const wasAdmin = (roleById(data, person.role) || {}).admin;
        const otherAdmins = data.people.some((p) => p.id !== person.id && p.status !== 'deactivated' && (roleById(data, p.role) || {}).admin);
        if (wasAdmin && !role.admin && person.status !== 'deactivated' && !otherAdmins) err.role = 'Bridge needs at least one Admin.';
        setErrors(err);
        if (Object.keys(err).length) return;

        const body = {};
        if (profileChanged.length) body.profile = { first: draft.first, last: draft.last, email, title: draft.title, dept: draft.dept };
        if (roleChanged || pageChanges.length) body.access = { role: draft.role, overrides: draft.overrides };
        setBusy(true);
        const res = await run(api.savePerson(person.id, body), <>Saved changes to <b>{`${draft.first.trim()} ${draft.last.trim()}`}</b>.</>);
        setBusy(false);
        if (res) closePanel();
    };

    const seeAll = () => {
        setActFilters({ q: fullName(person), type: 'all', range: 'all' });
        closePanel();
        goTab('activity');
    };

    const k = stKey(person);
    let line = '';
    let action = null;
    if (k === 'active') {
        line = 'Can sign in to Bridge.';
        if (!isMe) action = <button type="button" className="ua-btn ua-btn-sm" onClick={() => openModal({ type: 'deact', ids: [person.id] })}><Icon name="power" size={14} />Deactivate</button>;
    } else if (k === 'leaving') {
        line = `Access ends after ${fmtDate(dayMs(person.scheduledOff))}${person.schedReason ? ` (${person.schedReason})` : ''}.`;
        action = <button type="button" className="ua-btn ua-btn-sm" onClick={() => openModal({ type: 'deact', ids: [person.id] })}>Change</button>;
    } else if (k === 'invited') {
        line = `Hasn’t signed in yet.${person.invitedAt ? ` Invite copied ${rel(ms(person.invitedAt))}.` : ''}`;
        action = <button type="button" className="ua-btn ua-btn-sm" onClick={() => actions.shareInvite(person)}>Copy invite</button>;
    } else if (k === 'deactivated') {
        line = `Deactivated ${fmtDate(ms(person.deactivatedAt))}${person.deactReason ? ` · ${person.deactReason}` : ''}.`;
        action = <button type="button" className="ua-btn ua-btn-sm" onClick={() => openModal({ type: 'react', id: person.id })}><Icon name="power" size={14} />Reactivate</button>;
    }
    const history = data.log.filter((e) => e.target === person.id).slice(0, 5);

    const input = (key, label, opts = {}) => (
        <Field id={`ua-e-${key}`} label={label} error={errors[key]} full={opts.full}>
            <input
                id={`ua-e-${key}`} className="ua-inp" type={opts.type || 'text'} value={draft[key]} disabled={opts.disabled}
                maxLength={opts.max || 60} data-autofocus={key === 'first' ? true : undefined} onChange={(e) => edit(key, e.target.value)}
            />
        </Field>
    );

    return (
        <Sheet onClose={requestClose} labelledBy="ua-p-title">
            <div className="ua-p-head">
                <Avatar person={person} size={52} isMe={isMe} />
                <div className="ua-p-tx">
                    <h2 id="ua-p-title">{fullName(person)}{isMe ? <span className="ua-you">You</span> : null}</h2>
                    <div className="ua-p-sub">{person.email}</div>
                </div>
                <PersonStatus person={person} />
                <button type="button" className="ua-ib" onClick={requestClose} aria-label="Close"><Icon name="x" size={18} /></button>
            </div>

            <div className="ua-p-body">
                <section className="ua-p-sec">
                    <h3>Details</h3>
                    <div className="ua-grid2">
                        {input('first', 'First name')}
                        {input('last', 'Last name')}
                        {input('email', 'Work email', { type: 'email', full: true, disabled: isMe, max: 120 })}
                        {input('title', 'Job title', { max: 80, full: true })}
                        <DeptPicker
                            id="ua-e-dept" label="Departments" departments={catalog.departments} value={draft.dept}
                            onChange={(v) => edit('dept', v)} error={errors.dept}
                        />
                    </div>
                </section>

                <section className="ua-p-sec">
                    <h3>Role and access</h3>
                    <Field
                        id="ua-e-role" label="Role" error={errors.role}
                        help={isMe ? 'You can’t change your own role. Another Admin can.' : role.desc}
                    >
                        <select id="ua-e-role" className="ua-sel" value={draft.role} disabled={isMe} onChange={(e) => setRole(e.target.value)}>
                            {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </Field>
                    <div className="ua-acc-sum">
                        <span>
                            <b>{openCount(data, catalog, draft.role, draft.overrides)} of {pages.length} pages</b>
                            <small>
                                {role.admin ? 'Admins can open every page.'
                                    : custom ? `${plural(custom, 'page')} changed just for ${first}` : `Uses the ${role.name} pages`}
                            </small>
                        </span>
                        {!role.admin ? (
                            <button type="button" className="ua-btn ua-btn-sm" aria-expanded={showPages} onClick={() => setShowPages((s) => !s)}>
                                {showPages ? 'Hide pages' : 'Change pages'}
                            </button>
                        ) : null}
                    </div>
                    {showPages && !role.admin ? (
                        <>
                            <div className="ua-pages">
                                {catalog.sections.map((s) => (
                                    <React.Fragment key={s.id}>
                                        <div className="ua-sec-name">{s.name}</div>
                                        {s.pages.map((pg) => {
                                            const base = role.levels[pg.id] || 0;
                                            const level = lvlOf(data, draft.role, draft.overrides, pg.id);
                                            const changed = level !== base;
                                            return (
                                                <div key={pg.id} className={`ua-page-row${changed ? ' ua-changed' : ''}`}>
                                                    <span className="ua-pn">
                                                        {pg.name}
                                                        {changed ? <small>Changed for {first} · {role.name} has it {LV[base].toLowerCase()}</small> : null}
                                                    </span>
                                                    <PageSwitch label={pg.name} on={level} onChange={(v) => setPage(pg.id, v)} />
                                                </div>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </div>
                            {custom ? (
                                <div className="ua-mt">
                                    <button type="button" className="ua-link" onClick={() => { setDraft((d) => ({ ...d, overrides: {} })); setConfirm(false); }}>
                                        Reset to {role.name} pages
                                    </button>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </section>

                <section className="ua-p-sec">
                    <h3>Account</h3>
                    <div className="ua-acct">
                        <span>
                            <PersonStatus person={person} />
                            <small>{line}{k !== 'invited' ? ` Added ${fmtDate(ms(person.createdAt))}.` : ` Added ${rel(ms(person.createdAt))}.`}</small>
                        </span>
                        {action}
                    </div>
                </section>

                <section className="ua-p-sec">
                    <h3>History<button type="button" className="ua-link" onClick={seeAll}>See all</button></h3>
                    {history.length ? (
                        <ul className="ua-hist">
                            {history.map((e) => (
                                <li key={e.id}>
                                    <time dateTime={e.at}>{fmtShort(ms(e.at))}</time>
                                    <span>{labelOf(e.type)} by {actorLabel(e, data)}<Changes entry={e} /></span>
                                </li>
                            ))}
                        </ul>
                    ) : <span className="ua-muted">No changes yet.</span>}
                </section>
            </div>

            <div className="ua-p-foot">
                {confirm ? (
                    <>
                        <span className="ua-p-msg ua-dirty">Discard your changes?</span>
                        <button type="button" className="ua-btn ua-btn-sm" onClick={() => setConfirm(false)}>Keep editing</button>
                        <button type="button" className="ua-btn ua-btn-sm ua-btn-danger" onClick={closePanel}>Discard</button>
                    </>
                ) : (
                    <>
                        <span className={`ua-p-msg${nChanges ? ' ua-dirty' : ''}`}>{nChanges ? plural(nChanges, 'unsaved change') : 'No changes'}</span>
                        <button type="button" className="ua-btn ua-btn-sm" onClick={requestClose}>Cancel</button>
                        <button type="button" className="ua-btn ua-btn-sm ua-btn-primary" onClick={save} disabled={!nChanges || busy}>Save changes</button>
                    </>
                )}
            </div>
        </Sheet>
    );
}
